import React, {useState, useEffect, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import {classNames} from "primereact/utils";
import {InputText} from "primereact/inputtext";
import {Field, Form} from "react-final-form";
import {addLocale} from "primereact/api";
import {Panel} from "primereact/panel";
import {Password} from "primereact/password";
import {Button} from "primereact/button";
import {Calendar} from "primereact/calendar";
import {InputMask} from "primereact/inputmask";
import {Dropdown} from "primereact/dropdown";
import {FileUpload} from "primereact/fileupload";
import moment from "moment/moment";
import axios from "axios";
import Cookies from "js-cookie";

import LanglinksComponent from "../components/LanglinksComponent";
import {Message} from "primereact/message";
import felhomatracLogo from "../assets/images/logo-felhomatrac.png";
import felhomatracLogoDark from "../assets/images/logo-felhomatrac-dark.png";

const _FM_PublicchekinPage = (props) => {

  const {t, i18n} = props;
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({
    id_type: 'PASSPORT',
  });
  const [newFormData, setNewFormData] = useState({});
  const [reservation, setReservation] = useState({});
  const [reservations, setReservations] = useState([]);
  const [billingData, setBillingData] = useState({});
  const [customers, setCustomers] = useState([]);
  const [requiredFields, setRequiredFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customAnswersReservation, setCustomAnswersReservation] = useState({});
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [documentScanType, setDocumentScanType] = useState('id');
  const [documentScanCountry, setDocumentScanCountry] = useState('hu');
  const [primaryColor, setPrimaryColor] = useState('');
  const [primaryAltColor, setPrimaryAltColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [secondaryAltColor, setSecondaryAltColor] = useState('');
  const documentUploadRef = useRef(null);

  const getFirstNonEmptyValue = (data, keys = []) => {
    for (const key of keys) {
      const value = data?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  };

  const splitFullName = (fullName = '', options = {}) => {
    const {familyNameFirst = false} = options;
    const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) {
      return {firstname: '', lastname: ''};
    }

    const parts = normalized.split(' ');
    if (parts.length === 1) {
      return {firstname: parts[0], lastname: ''};
    }

    if (familyNameFirst) {
      return {
        firstname: parts.slice(1).join(' '),
        lastname: parts[0]
      };
    }

    return {
      firstname: parts[0],
      lastname: parts.slice(1).join(' ')
    };
  };

  const normalizeCountryCode = (value) => {
    if (value === undefined || value === null) {
      return undefined;
    }

    const rawValue = value.toString().trim();
    if (!rawValue) {
      return undefined;
    }

    const normalizedUpper = rawValue.toUpperCase();
    const normalizedLower = rawValue.toLowerCase();

    const byCode = countries.find((country) => (country?.code || '').toString().toUpperCase() === normalizedUpper);
    if (byCode?.code) {
      return byCode.code;
    }

    const byAlpha3 = countries.find((country) => {
      const alpha3 = country?.alpha3 || country?.alpha3_code || country?.iso3 || '';
      return alpha3.toString().toUpperCase() === normalizedUpper;
    });
    if (byAlpha3?.code) {
      return byAlpha3.code;
    }

    const byName = countries.find((country) => (country?.name || '').toString().toLowerCase() === normalizedLower);
    if (byName?.code) {
      return byName.code;
    }

    if (rawValue.length <= 3) {
      return normalizedUpper;
    }

    return rawValue;
  };

  const applyScannedCustomerData = (scanData = {}) => {
    const fields = scanData?.fields || {};
    const issuingCountryRaw = getFirstNonEmptyValue(fields, ['issuingCountry', 'issuing_country'])
      ?? getFirstNonEmptyValue(scanData, ['issuingCountry', 'issuing_country']);
    const scanCountryRaw = getFirstNonEmptyValue(scanData, ['country']) || issuingCountryRaw;
    const scanCountry = normalizeCountryCode(scanCountryRaw);
    const citizenshipCountry = normalizeCountryCode(issuingCountryRaw);
    const familyNameFirstCountries = ['cn', 'hu', 'jp', 'kp', 'kr', 'mn', 'tw', 'vn'];
    const scanIndicatesFamilyNameFirst = familyNameFirstCountries.includes((scanCountry || '').toString().toLowerCase());
    const scannedFirstnameDirect = getFirstNonEmptyValue(fields, ['firstName', 'firstname', 'givenName', 'givenNames', 'forename', 'forenames', 'name']);
    const scannedLastnameDirect = getFirstNonEmptyValue(fields, ['lastName', 'lastname', 'surname', 'familyName', 'familyNames']);
    const {firstname: scannedFirstnameFromFullName, lastname: scannedLastnameFromFullName} = splitFullName(
      fields?.fullName || '',
      {familyNameFirst: scanIndicatesFamilyNameFirst}
    );
    const scannedFirstname = scannedFirstnameDirect || scannedFirstnameFromFullName;
    const scannedLastname = scannedLastnameDirect || scannedLastnameFromFullName;
    const normalizedBirthDateRaw = getFirstNonEmptyValue(fields, ['dateOfBirth']);
    const normalizedBirthDate = normalizedBirthDateRaw
      ? getValidatedBirthDate(moment.utc(normalizedBirthDateRaw).local().format(t('YYYY-MM-DD')))
      : undefined;

    const normalizedGender = (getFirstNonEmptyValue(fields, ['gender']) || '').toString().toUpperCase();
    const genderMap = {
      M: 'MALE',
      MALE: 'MALE',
      F: 'FEMALE',
      FEMALE: 'FEMALE',
      O: 'OTHER',
      X: 'OTHER',
      OTHER: 'OTHER',
    };

    const scannedIdType = (getFirstNonEmptyValue(scanData, ['scanType']) || '')
      .toString()
      .toUpperCase();
    const idTypeMap = {
      ID: 'ID_CARD',
      DRIVE_LICENSE: 'DRIVER_LICENSE',
      DRIVER_LICENSE: 'DRIVER_LICENSE',
    };

    setFormData((prev) => {
      const nextData = {
        ...prev,
        firstname: scannedFirstname || prev.firstname,
        lastname: scannedLastname || prev.lastname,
        id_number: getFirstNonEmptyValue(fields, ['documentNumber']) ?? prev.id_number,
        address: getFirstNonEmptyValue(fields, ['address']) ?? prev.address,
        citizenship: scanCountry || citizenshipCountry || prev.citizenship,
        residence_country: scanCountry || prev.residence_country,
      };

      if (normalizedBirthDate !== undefined) {
        nextData.birth_date = normalizedBirthDate;
      }
      if (normalizedGender) {
        nextData.gender = genderMap[normalizedGender] || 'OTHER';
      }
      if (idTypeMap[scannedIdType]) {
        nextData.id_type = idTypeMap[scannedIdType];
      }

      return nextData;
    });
  };

  const onDocumentScanUpload = (e) => {
    const file = e?.files?.[0];
    if (!file) {
      return;
    }
    setDocumentUploadLoading(true);

    const upload = async () => {
      let uploadFile = file;
      if ((file.type || '').toLowerCase().startsWith('image/')) {
        try {
          const image = await new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              resolve(img);
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error('Failed to decode image file'));
            };
            img.src = objectUrl;
          });

          const maxDimension = 2200;
          const widthRatio = maxDimension / (image.width || maxDimension);
          const heightRatio = maxDimension / (image.height || maxDimension);
          const ratio = Math.min(1, widthRatio, heightRatio);
          const targetWidth = Math.max(1, Math.round((image.width || 1) * ratio));
          const targetHeight = Math.max(1, Math.round((image.height || 1) * ratio));

          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('Canvas context is unavailable');
          }
          context.drawImage(image, 0, 0, targetWidth, targetHeight);

          let quality = 0.9;
          let jpegBlob = null;
          const maxProcessedSize = 5 * 1024 * 1024;
          while (quality >= 0.55) {
            // eslint-disable-next-line no-await-in-loop
            const attemptBlob = await new Promise((resolve, reject) => {
              canvas.toBlob((blob) => {
                if (!blob) {
                  reject(new Error('Failed to encode image'));
                  return;
                }
                resolve(blob);
              }, 'image/jpeg', quality);
            });

            jpegBlob = attemptBlob;
            if (attemptBlob.size <= maxProcessedSize) {
              break;
            }
            quality -= 0.1;
          }

          if (!jpegBlob) {
            throw new Error('Failed to process image');
          }

          const safeName = (file.name || 'document')
            .replace(/\.[^/.]+$/, '')
            .replace(/[^\w.-]/g, '_');
          uploadFile = new File([jpegBlob], safeName + '.jpg', {type: 'image/jpeg'});

          if (uploadFile.size > 8 * 1024 * 1024) {
            window.App.toastShow('warn', t('warning'), 'A kiválasztott kép túl nagy. Kérlek kisebb felbontású képet tölts fel.');
            return;
          }
        } catch (conversionError) {
          window.App.toastShow('warn', t('warning'), 'A kép feldolgozása sikertelen. Kérlek JPG vagy PNG formátumot tölts fel.');
          return;
        }
      }

      const payload = new FormData();
      payload.append('file', uploadFile);
      payload.append('type', documentScanType || 'id');
      const normalizedCountry = ((documentScanCountry || 'hu') + '').toLowerCase();
      payload.append('country', normalizedCountry);

      return axios.post(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST
        + "public/reservation/" + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password + "/document-scan",
        payload,
        {
          headers: {
            "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
            "Preferred-locale": Cookies.get("language"),
          }
        }
      )
        .then(response => {
          const scannedData = response?.data?.data || response?.data || {};
          applyScannedCustomerData(scannedData);
          window.App.toastShow('success', t('success'), response?.data?.message || t('saved'));
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
        });
    };

    upload()
      .finally(() => {
        setDocumentUploadLoading(false);
        if (documentUploadRef.current) {
          documentUploadRef.current.clear();
        }
      });
  };

  const getValidatedBirthDate = (value) => {
    if (!value) {
      return null;
    }

    const formattedBirthDate = moment.utc(value).local().format(t('YYYY-MM-DD'));
    const parsedBirthDate = moment(formattedBirthDate, t('YYYY-MM-DD'), true);
    if (!parsedBirthDate.isValid()) {
      return undefined;
    }

    const minAllowedBirthYear = moment().year() - 99;
    if (parsedBirthDate.year() < minAllowedBirthYear) {
      window.App.customToastShow('warn', t('warning'), t('birth_date_too_old_warning'));
      return undefined;
    }

    return formattedBirthDate;
  };

  const validate = (data) => {
    let errors = {};
    if (requiredFields.includes('zipCode') && !data.zip_code) {
      errors.zip_code = 'Kötelező mező';
    }
    if (requiredFields.includes('firstname') && !data.firstname) {
      errors.firstname = 'Kötelező mező';
    }
    if (requiredFields.includes('lastname') && !data.lastname) {
      errors.lastname = 'Kötelező mező';
    }
    if (requiredFields.includes('gender') && !data.gender) {
      errors.gender = 'Kötelező mező';
    }
    if (requiredFields.includes('citizenship') && !data.citizenship) {
      errors.citizenship = 'Kötelező mező';
    }
    if (requiredFields.includes('residenceCountry') && !data.residence_country) {
      errors.residence_country = 'Kötelező mező';
    }
    if (requiredFields.includes('birthDate') && !data.birth_date) {
      errors.birth_date = 'Kötelező mező';
    }
    if (data.birth_date) {
      const parsedBirthDate = moment(data.birth_date, t('YYYY-MM-DD'), true);
      const minAllowedBirthYear = moment().year() - 99;
      if (parsedBirthDate.isValid() && parsedBirthDate.year() < minAllowedBirthYear) {
        errors.birth_date = t('birth_date_too_old_warning');
      }
    }
    if (requiredFields.includes('birthPlace') && !data.birth_place) {
      errors.birth_place = 'Kötelező mező';
    }
    if (requiredFields.includes('idType') && !data.id_type) {
      errors.id_type = 'Kötelező mező';
    }
    if (requiredFields.includes('idNumber') && !data.id_number) {
      errors.id_number = 'Kötelező mező';
    }
    if (requiredFields.includes('zipCode') && !data.zip_code) {
      errors.zip_code = 'Kötelező mező';
    }
    if (requiredFields.includes('address') && !data.address) {
      errors.address = 'Kötelező mező';
    }
    if (requiredFields.includes('companyName') && !data.company_name) {
      errors.company_name = 'Kötelező mező';
    }
    if (requiredFields.includes('email') && !data.email) {
      errors.email = 'Kötelező mező';
    }
    if (requiredFields.includes('phone') && !data.phone) {
      errors.phone = 'Kötelező mező';
    }
    if (requiredFields.includes('note') && !data.note) {
      errors.note = 'Kötelező mező';
    }
    return errors;
  };

  const onSubmit = (data, form) => {
    if (reservation.number_of_guests > customers.length) {
      setLoading(true);
      axios.post(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "public/reservation/customer/"
        + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password,
        formData,
        {
          params: {},
          headers: {
            "Preferred-locale": Cookies.get("language"),
            "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
          }
        })
        .then(response => {
          setLoading(false)
          const resetCustomFieldValues = customFields.reduce((acc, field) => {
            if (field?.name) {
              acc[field.name] = '';
            }
            return acc;
          }, {});
          setFormData({
            firstname: '',
            lastname: '',
            gender: '',
            citizenship: '',
            residence_country: '',
            birth_date: '',
            birth_place: '',
            id_type: 'PASSPORT',
            id_number: '',
            zip_code: '',
            address: '',
            company_name: '',
            email: '',
            phone: '',
            note: '',
            ...resetCustomFieldValues
          })
          window.App.toastShow('success', t('success'), response.data.message)
          axios.get(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "public/reservation/"
            + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password,
            {
              params: {},
              headers: {
                "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
                "Preferred-locale": Cookies.get("language")
              }
            })
            .then(response => {
              setReservation(response.data.data)
              //setFormData({...formData, reservation: response.data.data.id})
              let _customers = []
              let unit_name = reservation?.residential_unit?.name || reservation?.berth?.name
              response.data.data.customers.map((customer, index) => {
                _customers.push({
                  ...customer,
                  residential_unit: unit_name,
                })
              })
              setCustomers(_customers)
              setLoading(false)
            })
            .catch(error => {
              setLoading(false)
            })
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
        })
    } else {
      window.App.toastShow('warn', t('warning'), t('maximun_guests_reached'))
    }
    form.restart();
  };

  const isFormFieldValid = (meta) => !!(meta.touched && meta.error);
  const getFormErrorMessage = (meta) => {
    return isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>;
  };

  const triggerBackendFieldErrorClear = (fieldId) => {
    if (!fieldId) {
      return;
    }
    const fields = Array.from(document.querySelectorAll(`[id="${fieldId}"]`));
    if (!fields.length) {
      return;
    }
    const targetField = fields.find((element) => element.offsetParent !== null) || fields[fields.length - 1];
    if (!targetField) {
      return;
    }
    targetField.dispatchEvent(new Event('input', {bubbles: true}));
    targetField.dispatchEvent(new Event('change', {bubbles: true}));
  };

  const hexToRgb = (hex) => {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex.split("").map((char) => char + char).join("");
    }
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b]; // Eredmény RGB formátumban
  }

  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) {
      h = s = 0; // szürke
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          break;
      }
      h /= 6;
    }
    return [h * 360, s * 100, l * 100]; // HSL értékek (0-360, 0-100%, 0-100%)
  }

  const isDarkColor = (color) => {
    let rgb;
    if (color.startsWith("#")) {
      rgb = hexToRgb(color);
    } else if (color.startsWith("rgb")) {
      rgb = color.match(/\d+/g).map(Number);
    } else {
      throw new Error("Invalid color format! Use HEX (#RRGGBB) or RGB (rgb(r, g, b))");
    }
    const [, , l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
    return l < 50;
  }

  const getReadableTextColor = (backgroundColor) => {
    try {
      let rgb;
      if (backgroundColor.startsWith("#")) {
        rgb = hexToRgb(backgroundColor);
      } else if (backgroundColor.startsWith("rgb")) {
        rgb = (backgroundColor.match(/\d+(\.\d+)?/g) || []).slice(0, 3).map(Number);
      } else {
        return "#fff";
      }

      if (!rgb || rgb.length < 3 || rgb.some((value) => Number.isNaN(value))) {
        return "#fff";
      }

      const toLinear = (channel) => {
        const normalized = channel / 255;
        return normalized <= 0.03928
          ? normalized / 12.92
          : Math.pow((normalized + 0.055) / 1.055, 2.4);
      };

      const luminance = (0.2126 * toLinear(rgb[0])) + (0.7152 * toLinear(rgb[1])) + (0.0722 * toLinear(rgb[2]));
      const whiteContrast = 1.05 / (luminance + 0.05);
      const darkContrast = (luminance + 0.05) / 0.05;

      return whiteContrast >= darkContrast ? "#fff" : "#111";
    } catch (error) {
      return "#fff";
    }
  }

  addLocale('default', {
    firstDayOfWeek: 1,
    dayNames: [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')],
    dayNamesShort: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    dayNamesMin: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    monthNames: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    monthNamesShort: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    today: t('today'),
    clear: t('delete')
  });


  useEffect(() => {
    document.title = t('checkin_form') + ' - ' + process.env.REACT_APP_TITLE;
    document.getElementById('root').classList.add('public')
    if (window.App.urlVars().uniqueId && window.App.urlVars().password) {
      axios.get(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "public/reservation/"
        + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password,
        {
          params: {},
          headers: {
            "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
            "Preferred-locale": Cookies.get("language")
          }
        })
        .then(response => {
          setReservation({
            ...response.data.data,
            default_billing_data: response.data.data?.reservation_group?.default_billing_data,
          })
          if (response.data.data.customers && response.data.data.customers[0]) {
            let _customers = []
            let unit_name = reservation?.residential_unit?.name || reservation?.berth?.name
            response.data.data.customers.map((customer, index) => {
              _customers.push({
                ...customer,
                residential_unit: unit_name,
              })
            })
            setCustomers(_customers)
          }
          if (response.data.data && response.data.data
            && response.data.data.guest_information_request_page
            && response.data.data.guest_information_request_page.customer_required_fields) {
            setRequiredFields(response.data.data.guest_information_request_page.customer_required_fields)
          }
          if (response.data.data && response.data.data
            && response.data.data.guest_information_request_page
            && response.data.data.guest_information_request_page.girp_additional_fields) {
            setCustomFields(response.data.data.guest_information_request_page.girp_additional_fields)
          }
          if (response?.data?.data?.guest_information_request_page?.primary_color) {
            setPrimaryColor(
              response.data.data.guest_information_request_page?.primary_color)
            setPrimaryAltColor(getReadableTextColor(
              response.data.data.guest_information_request_page?.primary_color
            ));
          }
          if (response?.data?.data?.guest_information_request_page?.secondary_color) {
            setSecondaryColor(response.data.data.guest_information_request_page.secondary_color);
            document.documentElement.style.backgroundColor = response.data.data.guest_information_request_page.secondary_color;
            if (isDarkColor(response.data.data.guest_information_request_page.secondary_color)) {
              setSecondaryAltColor("#fff");
            }
          }
          setFormData({...formData, reservation: response.data.data.id})
          setBillingData(response.data.data.reservation_group?.default_billing_data)
          setCustomAnswersReservation(response.data.data.additional_answers || {})
          setLoading(false)
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
    }
    axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "dictionary/country",
      params: {},
      headers: {
        "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
        "Preferred-locale": Cookies.get("language")
      }
    })
      .then(response => {
        const allCountries = response.data.data;
        const prioritizedCountries = JSON.parse(process.env.REACT_APP_PRIORITIZEDCOUNTRIES || "[]");
        const sortedCountries = [
          ...allCountries.filter((country) => prioritizedCountries.includes(country.code)),
          ...allCountries.filter((country) => !prioritizedCountries.includes(country.code)),
        ];
        setCountries(sortedCountries);
        const defaultCountry = sortedCountries.find((country) =>
          (country?.code || '').toLowerCase() === 'hu');
        if (defaultCountry?.code) {
          setDocumentScanCountry(defaultCountry.code);
        }
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
  }, [window.App.state.rerenderPublicchekin]);

  const hasCustomLogo = !!(
    reservation?.guest_information_request_page?.image_url
    && reservation?.guest_information_request_page?.image_url?.toString()?.includes('.')
  );

  return (
    <>
      {primaryColor &&
        <style>
          {"\
                  .p-button.p-button-success,\
                  .p-button.p-button-warning,\
                  .p-fileupload-choose.p-button,\
                  .p-datepicker-trigger.p-button {\
                    border-color:" + primaryColor + " !important;\
                    background-color:" + primaryColor + " !important;\
                  }\
                "}
        </style>
      }
      {primaryAltColor &&
        <style>
          {"\
                  .p-button-success,\
                  .p-button-warning,\
                  .p-fileupload-choose.p-button,\
                  .p-datepicker-trigger.p-button {\
                    color:" + primaryAltColor + " !important;\
                  }\
                "}
        </style>
      }
      {!window.App.urlVars().password ? (
        <div className="LoginPage p-component">
          {reservation?.guest_information_request_page?.image_url && reservation?.guest_information_request_page?.image_url?.toString()?.includes('.') ? (
            <img
              src={process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST.toString().replace("/api/", "")
                + reservation.guest_information_request_page?.image_url}
              className={""} style={{maxHeight: "58px", maxWidth: "200px", display: "block", margin: "2rem, auto"}}/>
          ) : (
            <>
              {Cookies.get('theme') === 'dark' ? (
                <img src={require(".." + process.env.REACT_APP_LOGO_FILE_DARK)}
                     style={{maxHeight: "58px", maxWidth: "200px", display: "block", margin: "2rem auto"}}/>
              ) : (
                <img src={require(".." + process.env.REACT_APP_LOGO_FILE)}
                     style={{maxHeight: "58px", maxWidth: "200px", display: "block", margin: "2rem auto"}}/>
              )}
            </>
          )}
          <Panel className={"p-my-5"}>
            <div className={"p-grid p-mb-4"}>
              <div className={"p-col-6 p-lg-6 p-xl-6 p-col-align-center"}>
                <h3 className={"p-m-0"}>
                  <i className={"pi pi-user"}></i> {t('web_checkin')}
                </h3>
              </div>
              <div className={"p-col-6 p-lg-6 p-xl-6 p-text-right"}>
                {/*<ThemeselectComponent/>*/}
                <span className={"p-ml-2"}>
                  <LanglinksComponent/>
                </span>
              </div>
            </div>
            <div className={"p-grid"}>
              <div className={"p-col-12"}>
                <div className="p-field p-fluid">
                  <label htmlFor="password">{t('password')}</label>
                  <Password id="password"
                            toggleMask
                            feedback={false}
                            value={reservation.password}
                            onChange={(e) => {
                              setPassword(e.target.value)
                            }}
                  />
                </div>
              </div>
            </div>
            <div className={"p-grid p-mt-2"}>
              <div className={"p-col-12 p-lg-6 p-xl-6 p-col-align-center"}>
              </div>
              <div className={"p-col-12 p-lg-6 p-xl-6 p-text-right"} tabIndex="4">
                <Button type="submit"
                        label={t('login')}
                        className="p-button-success"
                        icon={"pi pi-check"}
                        loading={loading}
                        onClick={() => {
                          const currentURL = window.location.href.split('?')[0];
                          const newURL = `${currentURL}?password=${encodeURIComponent(password)}`
                            + "&uniqueId=" + window.App.urlVars().uniqueId;
                          window.location.href = newURL;
                          window.location.reload();
                        }}
                />
              </div>
            </div>
          </Panel>
          {hasCustomLogo && (
            <div className={"p-grid footer"}>
              <div className={"p-col-12 p-text-center p-mt-3"}>
                <a href="https://felhomatrac.eu" target="_blank" rel="noopener noreferrer">
                  <img
                    src={Cookies.get('theme') === 'dark' ? felhomatracLogoDark : felhomatracLogo}
                    alt="Felhomatrac"
                    style={{maxHeight: "34px"}}
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="_FM_PublicchekinPage p-component p-m-lg-4">
          {reservation && reservation.status &&
            <>
              <Panel style={{maxWidth: "980px"}} className={"p-mx-auto"}>
                <div className={"p-grid"}>
                  <div className={"p-col-12 p-lg-4 p-xl-4 p-col-align-center"}>
                    {reservation?.guest_information_request_page?.image_url && reservation?.guest_information_request_page?.image_url?.toString()?.includes('.') ? (
                      <img
                        src={process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST.toString().replace("/api/", "")
                          + reservation.guest_information_request_page?.image_url}
                        className={"p-d-inline-block p-mt-2"} style={{maxHeight: "58px", maxWidth: "200px"}}/>
                    ) : (
                      <>
                        {Cookies.get('theme') === 'dark' ? (
                          <img src={require(".." + process.env.REACT_APP_LOGO_FILE_DARK)}
                               style={{maxHeight: "58px", maxWidth: "200px"}}/>
                        ) : (
                          <img src={require(".." + process.env.REACT_APP_LOGO_FILE)}
                               style={{maxHeight: "58px", maxWidth: "200px"}}/>
                        )}
                      </>
                    )}
                  </div>
                  <div className={"p-col-12 p-lg-4 p-xl-4 p-col-align-center p-text-center"}>
                    <h2 className={"p-my-0"}>{t('web_checkin')}</h2>
                  </div>
                  <div className={"p-col-12 p-lg-4 p-xl-4 p-col-align-center p-text-right"}>
                    <LanglinksComponent/>
                  </div>
                </div>
                <hr className={"p-mb-2"}/>
                <div className={"p-grid p-mt-2"}>
                  <div className={"p-col-12 p-lg-2"}>
                    <small className={"p-text-uppercase"}>
                      {t('reserved_from')}
                    </small>
                    <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                      {moment.utc(reservation.reserved_from).local().format(t('YYYY-MM-DD'))}
                    </p>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <small className={"p-text-uppercase"}>
                      {t('reserved_to')}
                    </small>
                    <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                      {moment.utc(reservation.reserved_to).local().format(t('YYYY-MM-DD'))}
                    </p>
                  </div>
                  <div className={"p-col-12 p-lg-4"}>
                    <small className={"p-text-uppercase"}>
                      {t('reservation_number')}
                    </small>
                    <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                      {reservation.public_id}
                    </p>
                  </div>
                  <div className={"p-col-12 p-lg-4"}>
                    <small className={"p-text-uppercase"}>
                      {t('status')}
                    </small>
                    <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                      {t(reservation.status)}
                    </p>
                  </div>
                </div>
                <hr className={"p-my-2"}/>
                {reservation.errors && reservation.errors[0] ? (
                  <>
                    <Message severity="warn" text={t('reservation_errror_checkin_disabled')}
                             className={"p-d-block p-mt-2"}/>
                  </>
                ) : (
                  <>
                    <div className={"reservation new p-px-4 p-py-2 p-mt-5"}>
                      <div className={"p-grid"}>
                        <div className={"p-col-12 p-lg-6 p-col-align-center"}>
                          <h3>{t('billing')}</h3>
                        </div>
                        <div className={"p-col-12 p-lg-6 p-col-align-center p-text-right"}>
                          <Button type="button"
                                  disabled={reservation.errors && reservation.errors[0]}
                                  onClick={() => {
                                    let _billingData = billingData
                                    let _customAnswersReservation = Object.entries(customAnswersReservation || {})
                                      .reduce((acc, [key, value]) => {
                                        if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
                                          acc[key] = value.value
                                        } else {
                                          acc[key] = value ?? ''
                                        }
                                        return acc
                                      }, {})
                                    delete _billingData?._class_name
                                    delete _billingData?._country_specifics
                                    delete _billingData?.id
                                    delete _billingData?.created_at_snapshot
                                    delete _billingData?.updated_at_snapshot
                                    axios.patch(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST
                                      + "public/reservation/"
                                      + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password,
                                      {default_billing_data: _billingData, ..._customAnswersReservation},
                                      {
                                        params: {},
                                        headers: {
                                          "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
                                          "Preferred-locale": Cookies.get("language")
                                        }
                                      })
                                      .then(response => {
                                        setLoading(false)
                                        window.App.toastShow('success', t('success'), response.data.message)
                                      })
                                      .catch(error => {
                                        setLoading(false)
                                        window.App.toastShow('error', t('error'), error.response.data.message,
                                          '', error.response, 'billing-data')
                                      })
                                  }}
                                  label={t('save')}
                                  loading={loading}
                                  icon={"pi pi-save"}
                                  className="p-button-success p-ml-2"/>
                        </div>
                      </div>
                      <div className={"p-grid"}>
                        <div className={"p-col-12 p-lg-6"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="name_or_company_name">{t('name_or_company_name')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-dataname_or_company_name"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         name_or_company_name: e.target.value
                                       })
                                     }}
                                     value={billingData?.name_or_company_name}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-2"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="vat_number">{t('vat_number')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-datavat_number"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         vat_number: e.target.value
                                       })
                                     }}
                                     value={billingData?.vat_number}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-4"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="email">{t('email')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-dataemail"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         email: e.target.value
                                       })
                                     }}
                                     value={billingData?.email}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-2"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="country">{t('country')}</label>
                            <span className="p-d-block">
                          <Dropdown options={countries}
                                    filter
                                    optionLabel="name"
                                    optionValue="code"
                                    id={"billing-datacountry"}
                                    emptyMessage={t('empty_message')}
                                    onChange={(e) => {
                                      setBillingData({
                                        ...billingData,
                                        country: e.value
                                      })
                                    }}
                                    value={billingData?.country}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-2"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="zip_code">{t('zip_code')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-datazip_code"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         zip_code: e.target.value
                                       })
                                       if (e.target.value.length >= 4) {
                                         window.App.zipCode(e.target.value, (city) => {
                                           setBillingData(prev => ({
                                             ...prev,
                                             city: city
                                           }));
                                           triggerBackendFieldErrorClear('billing-datacity');
                                         });
                                       }
                                     }}
                                     value={billingData?.zip_code}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-3"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="city">{t('city')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-datacity"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         city: e.target.value
                                       })
                                     }}
                                     value={billingData?.city}
                          />
                        </span>
                          </div>
                        </div>
                        <div className={"p-col-12 p-lg-5"}>
                          <div className="p-field p-fluid">
                            <label htmlFor="street_and_number">{t('street_and_number')}</label>
                            <span className="p-d-block">
                          <InputText id="billing-datastreet_and_number"
                                     onChange={(e) => {
                                       setBillingData({
                                         ...billingData,
                                         street_and_number: e.target.value
                                       })
                                     }}
                                     value={billingData?.street_and_number}
                          />
                        </span>
                          </div>
                        </div>
                      </div>
                      {customFields && customFields[0] &&
                        <>
                          <hr/>
                          <div className={"p-grid"}>
                            <div className={"p-col-12 p-lg-12 p-col-align-center"}>
                              <h3>
                                {t('other')}
                              </h3>
                            </div>
                          </div>
                          <div className={"p-grid"}>
                            {customFields.map((field, index) => {
                              if (field.related_to === 'RELATED_TO_RESERVATION_GROUP') {
                                return (
                                  <div className={"p-col-12 p-lg-6"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor={field.name}>{t(field.label)}</label>
                                      <span className="p-d-block">
                                      <InputText id={field.name}
                                                 onChange={(e) => {
                                                   setCustomAnswersReservation((prev) => ({
                                                     ...(prev || {}),
                                                     [field.name]: e.target.value
                                                   }))
                                                 }}
                                                 value={customAnswersReservation?.[field.name]?.value
                                                   || customAnswersReservation?.[field.name] || ''}
                                      />
                                    </span>
                                    </div>
                                  </div>
                                )
                              }
                            })}
                          </div>
                        </>
                      }
                    </div>
                    <Form onSubmit={onSubmit} initialValues={formData}
                          validate={validate} render={({handleSubmit}) => (
                      <form onSubmit={handleSubmit} className="">
                        <div className={"customer new p-px-4 p-py-2 p-mt-5"}>
                          <div className={"p-grid"}>
                            <div className={"p-col-12 p-lg-6 p-col-align-center"}>
                              <h3>{t('new_guest')}</h3>
                            </div>
                            <div className={"p-col-12 p-lg-6 p-col-align-center p-text-right"}>
                              <Button type="submit"
                                      disabled={(reservation.errors && reservation.errors[0]) || (reservation.number_of_guests <= customers.length)}
                                      label={t('add_new_guest')}
                                      loading={loading}
                                      icon={"pi pi-plus"}
                                      className="p-button-success p-ml-2"/>
                            </div>
                          </div>
                          {reservation.number_of_guests <= customers.length &&
                            <div className={"p-mb-3"}>
                              <Message severity="warn" text={t('maximun_guests_reached')} className={"p-d-block"}/>
                            </div>
                          }
                          <div className={"p-grid highlighted p-mt-2"}>
                            <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                              <div className="p-field p-fluid p-mb-0">
                                <Dropdown options={countries}
                                          filter
                                          optionLabel="name"
                                          optionValue="code"
                                          id={"document_scan_country"}
                                          emptyMessage={t('empty_message')}
                                          onChange={(e) => {
                                            setDocumentScanCountry(e.value)
                                          }}
                                          value={documentScanCountry}
                                />
                              </div>
                            </div>
                            <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                              <div className="p-field p-fluid p-mb-0">
                                <Dropdown options={[
                                  {
                                    name: t('id_card'),
                                    value: 'id',
                                  },
                                  {
                                    name: t('driver_license'),
                                    value: 'drive_license',
                                  }
                                ]}
                                          optionLabel="name"
                                          optionValue="value"
                                          id={"document_scan_type"}
                                          emptyMessage={t('empty_message')}
                                          onChange={(e) => {
                                            setDocumentScanType(e.value)
                                          }}
                                          value={documentScanType}
                                />
                              </div>
                            </div>
                            <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                              <div className="p-field p-fluid p-mb-0">
                                <FileUpload
                                  ref={documentUploadRef}
                                  mode="basic"
                                  auto
                                  chooseLabel={t('upload_id')}
                                  chooseOptions={{icon: documentUploadLoading ? 'pi pi-spin pi-spinner' : 'pi pi-camera', className: 'w-100'}}
                                  name="file"
                                  accept="image/*,.heic,.heif"
                                  disabled={documentUploadLoading}
                                  customUpload
                                  uploadHandler={onDocumentScanUpload}
                                />
                              </div>
                            </div>
                            <div className={"p-col-12"}>
                              <Message severity="info" className={"p-d-block p-m-0 p-border-secondary p-text-center"} text={t('id_photo_not_stored')}/>
                            </div>
                          </div>
                          <div className={"p-grid"}>
                            <div className={"p-col-12 p-lg-4"}>
                              <Field name="lastname" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="lastname"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('lastname')}
                                    &nbsp;
                                    {requiredFields.includes('lastname') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                              <InputText id="lastname" {...input}
                                         onChange={(e) => {
                                           setFormData({
                                             ...formData,
                                             lastname: e.target.value
                                           })
                                         }}
                                         value={formData.lastname}
                                         className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                            </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className={"p-col-12 p-lg-4"}>
                              <Field name="firstname" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="firstname"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('firstname')}
                                    &nbsp;
                                    {requiredFields.includes('firstname') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                              <InputText id="firstname" {...input}
                                         onChange={(e) => {
                                           setFormData({
                                             ...formData,
                                             firstname: e.target.value
                                           })
                                         }}
                                         value={formData.firstname}
                                         className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                            </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className={"p-col-12 p-lg-4"}>
                              <Field name="birth_date" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="birth_date"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('birth_date')}
                                    &nbsp;
                                    {requiredFields.includes('birthDate') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <div className="p-inputgroup calendar-icon-only">
                            <InputMask id={"birth_date"}
                                       mask={t('9999-99-99')}
                                       value={formData.birth_date}
                                       onChange={(e) => {
                                         if (e.value.indexOf("_") === -1) {
                                           if (e.value === '') {
                                             e.value = null
                                           }
                                           const validatedBirthDate = getValidatedBirthDate(e.value);
                                           if (validatedBirthDate === undefined) {
                                             return;
                                           }
                                           setFormData({
                                             ...formData,
                                             birth_date: validatedBirthDate
                                           });
                                         }
                                       }}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}
                            />
                            <Calendar
                              value={formData.birth_date ? moment(formData.birth_date, 'YYYY-MM-DD').toDate() : null}
                              locale="default"
                              dateFormat={t('YY-MM-DD')}
                              showIcon
                              onChange={(e) => {
                                const validatedBirthDate = getValidatedBirthDate(e.value);
                                if (validatedBirthDate === undefined) {
                                  return;
                                }
                                setFormData({
                                  ...formData,
                                  birth_date: validatedBirthDate
                                });
                              }}/>
                          </div>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-3">
                              <Field name="birth_place" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="birth_place"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('birth_place')}
                                    &nbsp;
                                    {requiredFields.includes('birthPlace') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                          <InputText id="birth_place" {...input}
                                     onChange={(e) => {
                                       setFormData({
                                         ...formData,
                                         birth_place: e.target.value
                                       });
                                     }}
                                     value={formData.birth_place}
                                     className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className={"p-col-12 p-lg-3"}>
                              <Field name="gender" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="gender"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('gender')}
                                    &nbsp;
                                    {requiredFields.includes('gender') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <Dropdown options={[
                              {
                                name: t('male'),
                                value: 'MALE',
                              },
                              {
                                name: t('female'),
                                value: 'FEMALE',
                              },
                              {
                                name: t('other'),
                                value: 'OTHER',
                              }
                            ]}
                                      optionLabel="name"
                                      optionValue="value"
                                      id={"gender"}
                                      emptyMessage={t('empty_message')}
                                      onChange={(e) => {
                                        setFormData({
                                          ...formData,
                                          gender: e.value
                                        })
                                      }}
                                      value={formData.gender}
                            />
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className={"p-col-12 p-lg-3"}>
                              <Field name="citizenship" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="citizenship"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('citizenship')}
                                    &nbsp;
                                    {requiredFields.includes('citizenship') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <Dropdown options={countries}
                                      filter
                                      optionLabel="name"
                                      optionValue="code"
                                      id={"citizenship"}
                                      emptyMessage={t('empty_message')}
                                      onChange={(e) => {
                                        setFormData({
                                          ...formData,
                                          citizenship: e.value
                                        })
                                      }}
                                      value={formData.citizenship}
                            />
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className={"p-col-12 p-lg-3"}>
                              <Field name="residence_country" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="residence_country"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('residence_country')}
                                    &nbsp;
                                    {requiredFields.includes('residenceCountry') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <Dropdown options={countries}
                                      filter
                                      optionLabel="name"
                                      optionValue="code"
                                      id={"residence_country"}
                                      emptyMessage={t('empty_message')}
                                      onChange={(e) => {
                                        setFormData({
                                          ...formData,
                                          residence_country: e.value
                                        })
                                      }}
                                      value={formData.residence_country}
                            />
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="expected_check_in_at" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="expected_check_in_at"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('arrival_date')}
                                  </label>
                                  <span className="p-d-block">
                            <div className="p-inputgroup calendar-icon-only">
                            <InputMask id={"expected_check_in_at"}
                                       mask={t('9999-99-99')}
                                       value={formData.expected_check_in_at}
                                       onChange={(e) => {
                                         if (e.value.indexOf("_") === -1) {
                                           if (e.value === '') {
                                             e.value = null
                                           }
                                           setFormData({
                                             ...formData,
                                             expected_check_in_at: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                           });
                                         }
                                       }}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}
                            />
                            <Calendar
                              value={formData.expected_check_in_at ? moment(formData.expected_check_in_at, 'YYYY-MM-DD').toDate() : null}
                              locale="default"
                              dateFormat={t('YY-MM-DD')}
                              showIcon
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  expected_check_in_at: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                });
                              }}/>
                          </div>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="id_type" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="id_type"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('id_type')}
                                    &nbsp;
                                    {requiredFields.includes('idType') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <Dropdown options={[
                              {
                                name: t('id_card'),
                                value: 'ID_CARD',
                              },
                              {
                                name: t('passport'),
                                value: 'PASSPORT',
                              },
                              {
                                name: t('driver_license'),
                                value: 'DRIVER_LICENSE',
                              },
                              {
                                name: t('other'),
                                value: 'OTHER',
                              }
                            ]}
                                      filter
                                      optionLabel="name"
                                      optionValue="value"
                                      id={"id_type"}
                                      emptyMessage={t('empty_message')}
                                      onChange={(e) => {
                                        setFormData({
                                          ...formData,
                                          id_type: e.value
                                        })
                                      }}
                                      value={formData.id_type}
                            />
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="id_number" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="id_number"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('id_number')}
                                    &nbsp;
                                    {requiredFields.includes('idNumber') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="id_number" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           id_number: e.target.value
                                         });
                                       }}
                                       value={formData.id_number}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-3">
                              <Field name="zip_code" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="zip_code"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('zip_code')}
                                    &nbsp;
                                    {requiredFields.includes('zipCode') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="zip_code" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           zip_code: e.target.value
                                         });
                                       }}
                                       value={formData.zip_code}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-9">
                              <Field name="address" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="address"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('address')}
                                    &nbsp;
                                    {requiredFields.includes('address') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="address" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           address: e.target.value
                                         });
                                       }}
                                       value={formData.address}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="company_name" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="company_name"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('company_name')}
                                    &nbsp;
                                    {requiredFields.includes('companyName') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="company_name" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           company_name: e.target.value
                                         });
                                       }}
                                       value={formData.company_name}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="email" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="email"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('email')}
                                    &nbsp;
                                    {requiredFields.includes('email') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="email" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           email: e.target.value
                                         });
                                       }}
                                       value={formData.email}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12 p-lg-4">
                              <Field name="phone" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="phone"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('phone')}
                                    &nbsp;
                                    {requiredFields.includes('phone') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="phone" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           phone: e.target.value
                                         });
                                       }}
                                       value={formData.phone}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                            <div className="p-col-12">
                              <Field name="note" render={({input, meta}) => (
                                <div className="p-field p-fluid">
                                  <label htmlFor="note"
                                         className={classNames({'p-error': isFormFieldValid(meta)})}>
                                    {t('note')}
                                    &nbsp;
                                    {requiredFields.includes('note') &&
                                      <>*</>
                                    }
                                  </label>
                                  <span className="p-d-block">
                            <InputText id="note" {...input}
                                       onChange={(e) => {
                                         setFormData({
                                           ...formData,
                                           note: e.target.value
                                         });
                                       }}
                                       value={formData.note}
                                       className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                          </span>
                                  {getFormErrorMessage(meta)}
                                </div>
                              )}/>
                            </div>
                          </div>
                          {customFields && customFields[0] &&
                            <>
                              <hr/>
                              <div className={"p-grid"}>
                                <div className={"p-col-12 p-lg-12 p-col-align-center"}>
                                  <h3>
                                    {t('other')}
                                  </h3>
                                </div>
                              </div>
                              <div className={"p-grid"}>
                                {customFields.map((field, index) => {
                                  if (field.related_to === 'RELATED_TO_CUSTOMER') {
                                    return (
                                      <div className={"p-col-12 p-lg-6"}>
                                        <div className="p-field p-fluid">
                                          <label htmlFor={field.name}>{t(field.label)}</label>
                                          <span className="p-d-block">
                                      <InputText id={field.name}
                                                 onChange={(e) => {
                                                   setFormData({
                                                     ...formData,
                                                     [field.name]: e.target.value
                                                   })
                                                 }}
                                                 value={formData[field.name]}
                                      />
                                    </span>
                                        </div>
                                      </div>
                                    )
                                  }
                                })}
                              </div>
                            </>
                          }
                        </div>
                      </form>
                    )}/>
                    {customers && customers[0] &&
                      <>
                        {customers.map((customer, index) => {
                          return (
                            <>
                              <div className={"customer p-px-4 p-py-2 p-mt-5"}>
                                <div className={"p-grid"}>
                                  <div className={"p-col-12 p-lg-6 p-col-align-center"}>
                                    <h3>{t('guest') + ' ' + (Number(index) + 1)}</h3>
                                  </div>
                                  <div className={"p-col-12 p-lg-6 p-xl-6 p-col-align-center p-text-right"}
                                       tabIndex="4">
                                    <Button type="submit"
                                            label={t('edit')}
                                            className="p-button-warning"
                                            icon={"pi pi-pencil"}
                                            loading={loading}
                                            onClick={() => {
                                              let _data = customers[index]
                                              let _id = _data.id
                                              //delete _data.id
                                              delete _data.residential_unit
                                              delete _data._class_name
                                              delete _data._country_specifics
                                              delete _data._under_edit_by
                                              delete _data._under_edit_by_name_snap
                                              delete _data._under_edit_from
                                              setLoading(true)
                                              axios.patch(process.env.REACT_APP_API_PROTOCOL
                                                + process.env.REACT_APP_API_HOST + "public/reservation/customer/"
                                                + window.App.urlVars().uniqueId + "/" + window.App.urlVars().password + "/" + _id,
                                                _data,
                                                {
                                                  params: {},
                                                  headers: {
                                                    "Sys-name": window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_'),
                                                    "Preferred-locale": Cookies.get("language")
                                                  }
                                                })
                                                .then(response => {
                                                  window.App.toastShow('success', t('success'), response.data.message)
                                                  window.App.setState({rerenderPublicchekin: Math.random()})
                                                  setLoading(false)
                                                })
                                                .catch(error => {
                                                  setLoading(false)
                                                  window.App.toastShow('error', t('error'), error.response.data.message,
                                                    '', error.response, 'pc-patch' + index)
                                                })
                                            }}
                                    />
                                  </div>
                                </div>
                                <div className={"p-grid"}>
                                  <div className={"p-col-12 p-lg-4"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor="lastname">{t('lastname')}</label>
                                      <span className="p-d-block">
                                  <InputText id={"pc-patch" + index + "lastname"}
                                             onChange={(e) => {
                                               const updatedCustomers = [...customers];
                                               updatedCustomers[index].lastname = e.target.value;
                                               setReservation({...reservation, customers: updatedCustomers});
                                             }}
                                             value={customer.lastname}
                                  />
                                </span>
                                    </div>
                                  </div>
                                  <div className={"p-col-12 p-lg-4"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor="firstname">{t('firstname')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "firstname"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].firstname = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.firstname}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="birth_date">{t('birth_date')}</label>
                                      <span className="p-d-block">
                                    <div className="p-inputgroup calendar-icon-only">
                                    <InputMask id={"pc-patch" + index + "birth_date"}
                                               mask={t('9999-99-99')}
                                               value={customer.birth_date}
                                               onChange={(e) => {
                                                 if (e.value.indexOf("_") === -1) {
                                                   if (e.value === '') {
                                                     e.value = null
                                                   }
                                                   const validatedBirthDate = getValidatedBirthDate(e.value);
                                                   if (validatedBirthDate === undefined) {
                                                     return;
                                                   }
                                                   const updatedCustomers = [...customers];
                                                   updatedCustomers[index].birth_date = validatedBirthDate;
                                                   setReservation({...reservation, customers: updatedCustomers});
                                                 }
                                               }}
                                    />
                                    <Calendar
                                      value={customer.birth_date ? moment(customer.birth_date, 'YYYY-MM-DD').toDate() : null}
                                      locale="default"
                                      dateFormat={t('YY-MM-DD')}
                                      showIcon
                                      onChange={(e) => {
                                        const validatedBirthDate = getValidatedBirthDate(e.value);
                                        if (validatedBirthDate === undefined) {
                                          return;
                                        }
                                        const updatedCustomers = [...customers];
                                        updatedCustomers[index].birth_date = validatedBirthDate;
                                        setReservation({...reservation, customers: updatedCustomers});
                                      }}/>
                                  </div>
                                </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-3">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="birth_place">{t('birth_place')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "birth_place"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].birth_place = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.birth_place}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className={"p-col-12 p-lg-3"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor="gender">{t('gender')}</label>
                                      <span className="p-d-block">
                                    <Dropdown options={[
                                      {
                                        name: t('male'),
                                        value: 'MALE',
                                      },
                                      {
                                        name: t('female'),
                                        value: 'FEMALE',
                                      },
                                      {
                                        name: t('other'),
                                        value: 'OTHER',
                                      }
                                    ]}
                                              optionLabel="name"
                                              optionValue="value"
                                              id={"pc-patch" + index + "gender"}
                                              emptyMessage={t('empty_message')}
                                              onChange={(e) => {
                                                const updatedCustomers = [...customers];
                                                updatedCustomers[index].gender = e.value;
                                                setReservation({...reservation, customers: updatedCustomers});
                                              }}
                                              value={customer.gender}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className={"p-col-12 p-lg-3"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor="citizenship">{t('citizenship')}</label>
                                      <span className="p-d-block">
                                  <Dropdown options={countries}
                                            filter
                                            optionLabel="name"
                                            optionValue="code"
                                            id={"pc-patch" + index + "citizenship"}
                                            emptyMessage={t('empty_message')}
                                            onChange={(e) => {
                                              const updatedCustomers = [...customers];
                                              updatedCustomers[index].citizenship = e.value;
                                              setReservation({...reservation, customers: updatedCustomers});
                                            }}
                                            value={customer.citizenship}
                                  />
                                </span>
                                    </div>
                                  </div>
                                  <div className={"p-col-12 p-lg-3"}>
                                    <div className="p-field p-fluid">
                                      <label htmlFor="residence_country">{t('residence_country')}</label>
                                      <span className="p-d-block">
                                    <Dropdown options={countries}
                                              filter
                                              optionLabel="name"
                                              optionValue="code"
                                              id={"pc-patch" + index + "residence_country"}
                                              emptyMessage={t('empty_message')}
                                              onChange={(e) => {
                                                const updatedCustomers = [...customers];
                                                updatedCustomers[index].residence_country = e.value;
                                                setReservation({...reservation, customers: updatedCustomers});
                                              }}
                                              value={customer.residence_country}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="expected_check_in_at">{t('arrival_date')}</label>
                                      <span className="p-d-block">
                                    <div className="p-inputgroup calendar-icon-only">
                                    <InputMask id={"pc-patch" + index + "expected_check_in_at"}
                                               mask={t('9999-99-99')}
                                               value={customer.expected_check_in_at}
                                               onChange={(e) => {
                                                 if (e.value.indexOf("_") === -1) {
                                                   if (e.value === '') {
                                                     e.value = null
                                                   }
                                                   const updatedCustomers = [...customers];
                                                   updatedCustomers[index].expected_check_in_at = moment.utc(e.value).local().format(t('YYYY-MM-DD'));
                                                   setReservation({...reservation, customers: updatedCustomers});
                                                 }
                                               }}
                                    />
                                    <Calendar
                                      value={customer.expected_check_in_at ? moment(customer.expected_check_in_at, 'YYYY-MM-DD').toDate() : null}
                                      locale="default"
                                      dateFormat={t('YY-MM-DD')}
                                      showIcon
                                      onChange={(e) => {
                                        const updatedCustomers = [...customers];
                                        updatedCustomers[index].expected_check_in_at = moment.utc(e.value).local().format(t('YYYY-MM-DD'));
                                        setReservation({...reservation, customers: updatedCustomers});
                                      }}/>
                                  </div>
                                </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="id_type">{t('id_type')}</label>
                                      <span className="p-d-block">
                                    <Dropdown options={[
                                      {
                                        name: t('id_card'),
                                        value: 'ID_CARD',
                                      },
                                      {
                                        name: t('passport'),
                                        value: 'PASSPORT',
                                      },
                                      {
                                        name: t('driver_license'),
                                        value: 'DRIVER_LICENSE',
                                      },
                                      {
                                        name: t('other'),
                                        value: 'OTHER',
                                      }
                                    ]}
                                              filter
                                              optionLabel="name"
                                              optionValue="value"
                                              id={"pc-patch" + index + "id_type"}
                                              emptyMessage={t('empty_message')}
                                              onChange={(e) => {
                                                const updatedCustomers = [...customers];
                                                updatedCustomers[index].id_type = e.value;
                                                setReservation({...reservation, customers: updatedCustomers});
                                              }}
                                              value={customer.id_type}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="id_number">{t('id_number')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "id_number"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].id_number = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.id_number}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-3">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="zip_code">{t('zip_code')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "zip_code"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].zip_code = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.zip_code}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-9">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="address">{t('address')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "address"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].address = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.address}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="company_name">{t('company_name')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "company_name"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].company_name = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.company_name}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="email">{t('email')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "email"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].email = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.email}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12 p-lg-4">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="phone">{t('phone')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "phone"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].phone = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.phone}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                  <div className="p-col-12">
                                    <div className="p-field p-fluid">
                                      <label htmlFor="note">{t('note')}</label>
                                      <span className="p-d-block">
                                    <InputText id={"pc-patch" + index + "note"}
                                               onChange={(e) => {
                                                 const updatedCustomers = [...customers];
                                                 updatedCustomers[index].note = e.target.value;
                                                 setReservation({...reservation, customers: updatedCustomers});
                                               }}
                                               value={customer.note}
                                    />
                                  </span>
                                    </div>
                                  </div>
                                </div>
                                {customFields && customFields[0] &&
                                  <>
                                    <hr/>
                                    <div className={"p-grid"}>
                                      <div className={"p-col-12 p-lg-12 p-col-align-center"}>
                                        <h3>
                                          {t('other')}
                                        </h3>
                                      </div>
                                    </div>
                                    <div className={"p-grid"}>
                                      {customFields.map((field, index_) => {
                                        if (field.related_to === 'RELATED_TO_CUSTOMER') {
                                          return (
                                            <div className={"p-col-12 p-lg-6"}>
                                              <div className="p-field p-fluid">
                                                <label htmlFor={field.name}>{t(field.label)}</label>
                                                <span className="p-d-block">
                                              <InputText id={field.name}
                                                         onChange={(e) => {
                                                           const updatedCustomers = [...customers];
                                                           updatedCustomers[index][field.name] = e.target.value;
                                                           setReservation({
                                                             ...reservation,
                                                             customers: updatedCustomers
                                                           });
                                                         }}
                                                         value={reservation.customers[index][field.name] || customer?.additional_answers?.[field.name]?.value || ''}
                                              />
                                            </span>
                                              </div>
                                            </div>
                                          )
                                        }
                                      })}
                                    </div>
                                  </>
                                }
                              </div>
                            </>
                          )
                        })}
                      </>
                    }
                  </>
                )}
                {hasCustomLogo && (
                  <>
                    <hr className={"p-mt-5"}/>
                    <div className={"p-grid footer"}>
                      <div className={"p-col-12 p-text-center p-mt-3"}>
                        <a href="https://felhomatrac.eu" target="_blank" rel="noopener noreferrer">
                          <img
                            src={Cookies.get('theme') === 'dark' ? felhomatracLogoDark : felhomatracLogo}
                            alt="Felhomatrac"
                            style={{maxHeight: "22px"}}
                          />
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </Panel>
            </>
          }
        </div>
      )}
    </>
  )
}
export default withTranslation('common')(_FM_PublicchekinPage);
